import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { StatusChip } from '@/components/ui/status-chip';
import { Plus, ChevronDown, ChevronUp, Square, AlertTriangle } from 'lucide-react';
import { getOrders, createOrder, stopOrder, type IpdMedicationOrder, type CreateOrderPayload } from '@/lib/ipdApi';
import { format } from 'date-fns';
import { usePermission } from '@/hooks/usePermission';
import DrugSearchInput from '@/components/shared/DrugSearchInput';
import type { DrugSearchResult } from '@/lib/api';

// ─── Constants ────────────────────────────────────────────────────────────────

const ROUTES   = ['oral', 'iv', 'im', 'sc', 'topical', 'inhalation', 'other'] as const;
const ROUTE_LABELS: Record<string, string> = {
  oral: 'Oral', iv: 'IV', im: 'IM', sc: 'SC', topical: 'Topical', inhalation: 'Inhalation', other: 'Other',
};

/** The order's route for a catalog drug's route (oral / injection / topical / inhalation / ophthalmic / otic) */
function routeFor(catalogRoute: string | null | undefined): string | null {
  switch ((catalogRoute ?? '').toLowerCase()) {
    case '':           return null;
    case 'oral':       return 'oral';
    case 'injection':  return 'iv';
    case 'topical':    return 'topical';
    case 'inhalation': return 'inhalation';
    default:           return 'other';
  }
}
// STAT (once, now) and HS (at bedtime) are everyday ward orders
const FREQS    = ['OD', 'BD', 'TDS', 'QID', 'Q6H', 'Q8H', 'HS', 'SOS', 'STAT'] as const;

// ─── Order Form ───────────────────────────────────────────────────────────────

interface OrderFormState {
  drug_id:               number | null;
  drug_name:             string;
  generic_name:          string;
  dose:                  string;
  route:                 string;
  frequency:             string;
  start_date:            string;
  end_date:              string;
  is_prn:                boolean;
  prn_reason:            string;
  special_instructions:  string;
  /** The patient brings or buys this medicine — the hospital doesn't bill it */
  not_billed:            boolean;
}

const today = () => format(new Date(), 'yyyy-MM-dd');

const emptyForm = (): OrderFormState => ({
  drug_id: null, drug_name: '', generic_name: '',
  dose: '', route: 'oral', frequency: 'BD',
  start_date: today(), end_date: '',
  is_prn: false, prn_reason: '', special_instructions: '', not_billed: false,
});

// ─── Small shared UI ──────────────────────────────────────────────────────────

function Select({
  value, onChange, options, labels, className,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
  labels?: Record<string, string>;
  className?: string;
}) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className={cn(
        'h-8 rounded-md border border-field bg-background px-2 text-sm',
        'focus:outline-none focus:ring-1 focus:ring-ring',
        className,
      )}
    >
      {options.map(o => (
        <option key={o} value={o}>{labels?.[o] ?? o}</option>
      ))}
    </select>
  );
}

// ─── Stop inline flow ─────────────────────────────────────────────────────────

function StopRow({
  order, onStop,
}: {
  order: IpdMedicationOrder;
  onStop: (orderId: string, reason: string) => Promise<void>;
}) {
  const [open,   setOpen]   = useState(false);
  const [reason, setReason] = useState('');
  const [busy,   setBusy]   = useState(false);
  const [error,  setError]  = useState('');

  const submit = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    setError('');
    try {
      await onStop(order.id, reason.trim());
      setOpen(false);
      setReason('');
    } catch (e: any) {
      setError(e?.message || 'Failed to stop order');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
        onClick={() => setOpen(true)}
      >
        <Square className="w-3 h-3 mr-1" /> Stop
      </Button>
    );
  }

  return (
    <div className="flex flex-col items-start gap-1">
      <div className="flex items-center gap-1">
        <Input
          autoFocus
          placeholder="Reason…"
          value={reason}
          onChange={e => setReason(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') setOpen(false); }}
          className="h-7 text-xs w-36"
        />
        <Button size="sm" className="h-7 px-2 text-xs" onClick={submit} disabled={busy || !reason.trim()}>
          {busy ? '…' : 'Confirm'}
        </Button>
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => { setOpen(false); setError(''); }}>
          ✕
        </Button>
      </div>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface Props {
  admissionId: string;
  isActive?: boolean;
}

export default function IPDMedicationOrdersTab({ admissionId, isActive = true }: Props) {
  const { can } = usePermission();
  // Ordering and stopping medicines is a doctor's act
  const isDoctor = can('ipd:clinical');

  const [orders,      setOrders]      = useState<IpdMedicationOrder[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [showStopped, setShowStopped] = useState(false);
  const [open,        setOpen]        = useState(false);
  const [form,        setForm]        = useState<OrderFormState>(emptyForm());
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getOrders(admissionId, 'all');
      setOrders(data ?? []);
    } catch (e) {
      console.error('Failed to load orders:', e);
    } finally {
      setLoading(false);
    }
  }, [admissionId]);

  useEffect(() => { load(); }, [load]);

  const active  = orders.filter(o => o.status === 'active');
  const stopped = orders.filter(o => o.status !== 'active');

  const set = <K extends keyof OrderFormState>(k: K) =>
    (v: OrderFormState[K]) => setForm(f => ({ ...f, [k]: v }));

  const handleDrugSelect = (drug: DrugSearchResult) => {
    setForm(f => ({
      ...f,
      drug_id:      drug.id ?? null,
      drug_name:    drug.brand_name,
      generic_name: drug.composition_raw ?? '',
      // Route from the catalog, so an injection isn't ordered "oral"; injections start as IV (IM / SC in the list)
      route:        routeFor(drug.route) ?? f.route,
    }));
  };

  const handleDrugCustom = (val: string) => {
    setForm(f => ({ ...f, drug_id: null, drug_name: val, generic_name: '' }));
  };

  const handleSave = async () => {
    setError('');
    if (!form.drug_name.trim())  { setError('Drug name is required.');    return; }
    if (!form.dose.trim())       { setError('Dose is required.');          return; }
    if (!form.frequency.trim())  { setError('Frequency is required.');     return; }
    if (!form.start_date)        { setError('Start date is required.');    return; }
    if (form.is_prn && !form.prn_reason.trim()) {
      setError('PRN reason is required when PRN is on.');
      return;
    }

    const payload: CreateOrderPayload = {
      drug_id:              form.drug_id,
      drug_name:            form.drug_name.trim(),
      generic_name:         form.generic_name.trim() || undefined,
      dose:                 form.dose.trim(),
      route:                form.route,
      frequency:            form.frequency.trim(),
      start_date:           form.start_date,
      end_date:             form.end_date || null,
      is_prn:               form.is_prn,
      prn_reason:           form.is_prn ? form.prn_reason.trim() : undefined,
      special_instructions: form.special_instructions.trim() || undefined,
      not_billed_reason:    form.not_billed ? "Patient's own medicine" : null,
    };

    try {
      setSaving(true);
      await createOrder(admissionId, payload);
      setOpen(false);
      setForm(emptyForm());
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to save order.');
    } finally {
      setSaving(false);
    }
  };

  const handleStop = async (orderId: string, reason: string) => {
    await stopOrder(admissionId, orderId, reason);
    await load();
  };

  // ── Shared table render ─────────────────────────────────────────────────────

  const OrderTable = ({ rows, showStopBtn }: { rows: IpdMedicationOrder[]; showStopBtn: boolean }) => (
    <div className="rounded-lg border border-border bg-card overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="h-8 border-b border-border bg-muted/40">
            {['Drug', 'Dose', 'Route', 'Frequency', 'Start', 'End', 'By', showStopBtn ? 'Action' : 'Status'].map((h, i) => (
              <th key={i} scope="col" className="px-3 text-left text-2xs font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => (
            <tr key={o.id} className="h-10 border-b border-divider last:border-0">
              <td className="px-3 py-2">
                <div className="flex flex-wrap items-baseline gap-x-1.5">
                  <span className="font-medium text-sm">{o.drug_name}</span>
                  {o.generic_name && o.generic_name !== o.drug_name && (
                    <span className="text-xs text-muted-foreground">{o.generic_name}</span>
                  )}
                  {o.is_prn ? (
                    <StatusChip tone="warning" className="self-center">PRN</StatusChip>
                  ) : null}
                  {o.not_billed_reason && (
                    <span className="text-xs text-muted-foreground" title="Not billed by the hospital">{o.not_billed_reason}</span>
                  )}
                </div>
              </td>
              <td className="px-3 py-2 whitespace-nowrap">{o.dose}</td>
              <td className={cn('px-3 py-2 whitespace-nowrap', o.route && o.route.length <= 3 ? 'uppercase' : 'capitalize')}>{o.route}</td>
              <td className="px-3 py-2 whitespace-nowrap">{o.frequency}</td>
              <td className="px-3 py-2 whitespace-nowrap text-xs tabular-nums">{o.start_date ? format(new Date(o.start_date), 'dd MMM') : '—'}</td>
              <td className="px-3 py-2 whitespace-nowrap text-xs tabular-nums">{o.end_date ? format(new Date(o.end_date), 'dd MMM') : '—'}</td>
              <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">{o.ordered_by_name}</td>
              <td className="px-3 py-1.5 whitespace-nowrap">
                {showStopBtn && isDoctor && isActive ? (
                  <StopRow order={o} onStop={handleStop} />
                ) : (
                  <StatusChip tone={o.status === 'stopped' ? 'alert' : o.status === 'completed' ? 'success' : 'accent'} className="capitalize">
                    {o.status}
                  </StatusChip>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {active.length} active order{active.length !== 1 ? 's' : ''}
        </div>
        {isDoctor && isActive && (
          <Button size="sm" onClick={() => { setForm(emptyForm()); setError(''); setOpen(true); }}>
            <Plus className="w-4 h-4 mr-1" /> New order
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
      ) : (
        <>
          {/* Active orders */}
          {active.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">No active orders.</div>
          ) : (
            <OrderTable rows={active} showStopBtn={true} />
          )}

          {/* Stopped / completed — collapsible */}
          {stopped.length > 0 && (
            <div>
              <button
                onClick={() => setShowStopped(s => !s)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                {showStopped ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {stopped.length} stopped / completed order{stopped.length !== 1 ? 's' : ''}
              </button>
              {showStopped && <OrderTable rows={stopped} showStopBtn={false} />}
            </div>
          )}
        </>
      )}

      {/* New Order Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New medication order</DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2">

            {/* Drug search */}
            <div>
              <label className="block text-[13px] font-semibold text-label mb-1">Drug</label>
              <div className="border rounded-md px-2 py-1.5 bg-background focus-within:ring-1 focus-within:ring-ring">
                <DrugSearchInput
                  value={form.drug_name}
                  onChange={handleDrugSelect}
                  onCustomInput={handleDrugCustom}
                  placeholder="Search or type drug name…"
                />
              </div>
            </div>

            {/* Dose */}
            <div>
              <label className="block text-[13px] font-semibold text-label mb-1">Dose</label>
              <Input
                value={form.dose}
                onChange={e => set('dose')(e.target.value)}
                placeholder="e.g. 500mg, 1 unit"
                className="h-8 text-sm"
              />
            </div>

            {/* Route + Frequency */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[13px] font-semibold text-label mb-1">Route</label>
                <Select value={form.route} onChange={set('route')} options={ROUTES} labels={ROUTE_LABELS} className="w-full" />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-label mb-1">Frequency</label>
                <Select value={form.frequency} onChange={set('frequency')} options={FREQS} className="w-full" />
              </div>
            </div>

            {/* Start + End date */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[13px] font-semibold text-label mb-1">Start date</label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={e => set('start_date')(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-label mb-1">End date <span className="text-muted-foreground/60">(optional)</span></label>
                <Input
                  type="date"
                  value={form.end_date}
                  min={form.start_date || undefined}
                  onChange={e => set('end_date')(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
            </div>

            {/* PRN toggle */}
            <div className="flex items-center gap-2">
              <input
                id="prn-toggle"
                type="checkbox"
                checked={form.is_prn}
                onChange={e => set('is_prn')(e.target.checked)}
                className="h-4 w-4 rounded border-field accent-primary"
              />
              <label htmlFor="prn-toggle" className="text-sm select-none cursor-pointer">PRN (as needed)</label>
            </div>

            {form.is_prn && (
              <div>
                <label className="block text-[13px] font-semibold text-label mb-1">PRN reason</label>
                <Input
                  value={form.prn_reason}
                  onChange={e => set('prn_reason')(e.target.value)}
                  placeholder="e.g. pain, fever"
                  className="h-8 text-sm"
                />
              </div>
            )}

            {/* Patient's own medicine: kept off the bill */}
            <div className="flex items-center gap-2">
              <input
                id="own-medicine"
                type="checkbox"
                checked={form.not_billed}
                onChange={e => set('not_billed')(e.target.checked)}
                className="h-4 w-4 rounded border-field accent-primary"
              />
              <label htmlFor="own-medicine" className="text-sm select-none cursor-pointer">Patient's own medicine (not billed)</label>
            </div>

            {/* Special instructions */}
            <div>
              <label className="block text-[13px] font-semibold text-label mb-1">
                Special instructions <span className="text-muted-foreground/60">(optional)</span>
              </label>
              <Input
                value={form.special_instructions}
                onChange={e => set('special_instructions')(e.target.value)}
                placeholder="e.g. give with food, dilute in 100ml NS"
                className="h-8 text-sm"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                {error}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
